import React, { useEffect, useRef, useState } from 'react';
import DocumentList from './index';
import { Document } from '../../types/Document';
import { DocumentListFilters } from '../../types/DocumentFilters';
import { fetchResidentGroupDocs } from '../../services/Documents';
import { mergeDocPage } from '../../utils/document';
import sendToast from '../../../utils/Toast';

interface Props {
    facilityId: string;
    residentId: string;
    groupId: string;
    groupLabel: string;
    filters: DocumentListFilters;
    pageSize: number;
    facilityTimezone: string;
    activeResidentId?: string;
    extraLoading?: boolean;
    onDelete: (doc: Document) => Promise<boolean>;
    // Set by the parent whenever any doc is deleted, from this section or another list
    // (e.g. My Docs) — lets this section drop the doc from its own local state even when
    // the delete happened elsewhere.
    deletedDoc?: { id: string } | null;
    onLoadingChange?: (groupId: string, loading: boolean) => void;
}

// Renders one real facility group's docs as its own "All Documents" section — mirrors
// the Community/My Docs sections, but scoped to a single group and paginated on its own.
const GroupDocsSection = ({
    facilityId,
    residentId,
    groupId,
    groupLabel,
    filters,
    pageSize,
    facilityTimezone,
    activeResidentId,
    extraLoading = false,
    onDelete,
    deletedDoc,
    onLoadingChange,
}: Props) => {
    const [docs, setDocs] = useState<Document[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [pageNo, setPageNo] = useState<number>(1);
    const [hasMore, setHasMore] = useState<boolean>(true);
    // Bumped on every fetch; a response only gets applied if it's still the latest one
    // requested. Without this, an in-flight fetch for a stale page/filter combo (e.g. one
    // issued right before the resident changed the search text) could resolve after — and
    // overwrite — a newer, already-applied response.
    const requestIdRef = useRef(0);

    // Clear this group's reported loading state on unmount (e.g. filtered out of view)
    // so it can't permanently block the parent's full-page loading overlay.
    useEffect(() => {
        return () => onLoadingChange?.(groupId, false);
    }, []);

    const fetchGroupDocs = async (page: number) => {
        const requestId = ++requestIdRef.current;
        try {
            setLoading(true);
            onLoadingChange?.(groupId, page === 1);
            const response = await fetchResidentGroupDocs(
                facilityId,
                residentId,
                page,
                pageSize,
                { ...filters, groupId },
            );
            if (requestId !== requestIdRef.current) return;
            setHasMore(response.Result.Content.TotRecords > page * pageSize);
            setDocs((prev) => mergeDocPage(prev, page, response.Result.Content.Result));
        } catch (error) {
            if (requestId !== requestIdRef.current) return;
            sendToast('error', error instanceof Error ? error.message : 'Something went wrong while fetching documents');
        } finally {
            if (requestId === requestIdRef.current) {
                setLoading(false);
                onLoadingChange?.(groupId, false);
            }
        }
    };

    // A single effect drives both "reset to page 1 on filter/group change" and "fetch the
    // requested page" — the old split-effect version reset `pageNo` to 1 in one effect and
    // fetched off the (still-stale, pre-reset) `pageNo` in a sibling effect during the same
    // render, so a filter change could re-fetch the old page number before the reset took
    // effect. Fetching directly with an explicit page number removes that race entirely.
    useEffect(() => {
        setPageNo(1);
        setHasMore(true);
        fetchGroupDocs(1);
    }, [facilityId, residentId, groupId, filters.searchText, filters.serviceId, filters.assetId]);

    useEffect(() => {
        if (pageNo === 1) return;
        fetchGroupDocs(pageNo);
    }, [pageNo]);

    useEffect(() => {
        if (!deletedDoc) return;
        setDocs((prev) => prev.filter((d) => String(d._id) !== String(deletedDoc.id)));
    }, [deletedDoc]);

    const handleDelete = async (doc: Document) => {
        const success = await onDelete(doc);
        if (success) {
            setDocs((prev) => prev.filter((d) => String(d._id) !== String(doc._id)));
        }
    };

    if (!loading && docs.length === 0) {
        return null;
    }

    return (
        <div className="docs-section">
            <div className="docs-section-label"><span className="docs-section-label-text">{groupLabel}</span></div>
            <DocumentList
                docs={docs}
                loading={loading || extraLoading}
                hasMore={hasMore}
                setPageNo={setPageNo}
                facilityTimezone={facilityTimezone}
                activeResidentId={activeResidentId}
                onDelete={handleDelete}
            />
        </div>
    );
};

export default GroupDocsSection;
